<script setup lang="ts">
import { ClauseValidationOptionType, PlaybookRiskLevel } from '@/api';
import type { PlaybookEvaluationOption } from '@/services/playbooks/playbookRunDisplay';
import GaiaRiskPill, { type GaiaRiskLevel } from '@/components/design/base/GaiaRiskPill.vue';
import GaiaTag from '@/components/design/base/GaiaTag.vue';
import GaiaText from '@/components/design/base/GaiaText.vue';
import { playbookRiskLevelMap } from '@/components/design/statusMaps';

defineProps<{
  matchedOptions: PlaybookEvaluationOption[];
}>();

/** How acceptable the clause the run matched is, on the risk pill's scale. */
const optionTypeRisk: Record<ClauseValidationOptionType, GaiaRiskLevel> = {
  [ClauseValidationOptionType.PREFERRED]: 'low',
  [ClauseValidationOptionType.FALLBACK]: 'medium',
  [ClauseValidationOptionType.ESCALATION]: 'high',
};

function optionType(option: PlaybookEvaluationOption): ClauseValidationOptionType | null {
  return 'option_type' in option ? (option.option_type ?? null) : null;
}

function clauseName(option: PlaybookEvaluationOption): string | null {
  return 'clause' in option ? (option.clause?.name ?? null) : null;
}

function formatOptionType(value: ClauseValidationOptionType): string {
  return value.charAt(0).toUpperCase() + value.slice(1);
}

/** Hide the option-type pill when the option name already conveys the same thing. */
function isTypeRedundant(option: PlaybookEvaluationOption): boolean {
  const type = optionType(option);
  return type !== null && option.name.trim().toLowerCase() === type.toLowerCase();
}
</script>

<template>
  <n-flex vertical :size="10">
    <n-flex
      v-for="option in matchedOptions"
      :key="option.uid"
      vertical
      :size="5"
      align="flex-start"
    >
      <n-flex align="center" :size="6">
        <gaia-risk-pill
          :level="playbookRiskLevelMap[option.risk_level ?? PlaybookRiskLevel.NONE].level"
        >
          {{ option.name }}
        </gaia-risk-pill>
        <gaia-risk-pill
          v-if="optionType(option) && !isTypeRedundant(option)"
          :level="optionTypeRisk[optionType(option)!]"
        >
          {{ formatOptionType(optionType(option)!) }}
        </gaia-risk-pill>
      </n-flex>
      <gaia-tag v-if="clauseName(option)" variant="muted">
        Matched clause: {{ clauseName(option) }}
      </gaia-tag>
      <gaia-text v-if="option.definition" size="row" tone="tertiary">
        {{ option.definition }}
      </gaia-text>
    </n-flex>
    <gaia-text v-if="matchedOptions.length === 0" tone="subtle">
      No acceptable clause found.
    </gaia-text>
  </n-flex>
</template>
